import {
  PATIENT_ARRIVED_DEFAULT_SOUND,
  PopupNotification,
} from '@principle-theorem/notifications';
import {
  AppointmentStatus,
  AppointmentSummary,
  ChecklistType,
  IChecklistItem,
  InteractionType,
  InvoiceStatus,
  ISchedulingEventData,
  isEventable,
  isStaffer,
  MentionResourceType,
  ParticipantType,
  PatientFormStatus,
  PrincipleCreatedBy,
  TreatmentStepStatus,
  type IAppointment,
  type IDepositMetadata,
  type IEvent,
  type IPatient,
  type IStaffer,
  type ITreatmentStep,
} from '@principle-theorem/principle-core/interfaces';
import {
  Firestore,
  snapshot,
  TIME_FORMAT,
  toMomentTz,
  toNamedDocument,
  type DocumentReference,
  type WithRef,
} from '@principle-theorem/shared';
import { combineLatest, of, type Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { TimezoneResolver } from '../../../timezone';
import { Appointment } from '../../appointment/appointment';
import { SchedulingEvent } from '../../appointment/scheduling-event';
import { AutomationEntity } from '../../automations-store/automation-entity';
import { stafferToNamedDoc } from '../../common';
import { Event } from '../../event/event';
import { Invoice } from '../../invoice/invoice';
import { OrganisationCache } from '../../organisation/organisation-cache';
import { Patient } from '../../patient/patient';
import { PatientForm } from '../../patient/patient-form';
import { Staffer } from '../../staffer/staffer';
import {
  convertWaitListDetailsToItem,
  type IWaitListDetails,
} from '../waitlist-details';
import { AppointmentRescheduleInteractionBuilder } from './appointment-reschedule-interaction-builder';

export interface IWithWaitList {
  waitListItem: IWaitListDetails;
}

export interface IWithEvent {
  event: IEvent;
}

export interface IWithAutomations {
  automations: AutomationEntity[];
}

export interface IRescheduleAppointmentRequest
  extends IWithEvent,
    Partial<IWithWaitList>,
    IWithAutomations {
  treatmentStep?: ITreatmentStep;
  schedulingEventData: ISchedulingEventData;
  depositMetadata?: IDepositMetadata;
  checklists?: IChecklistItem[];
}

export class AppointmentManager {
  static async move(
    owner: WithRef<IStaffer> | WithRef<IPatient>,
    patient: WithRef<IPatient>,
    appointment: WithRef<IAppointment>,
    data: IRescheduleAppointmentRequest,
    actor?: PrincipleCreatedBy
  ): Promise<DocumentReference<IAppointment>> {
    const stafferNamedDoc = isStaffer(owner)
      ? stafferToNamedDoc(owner)
      : undefined;
    const timezone = await TimezoneResolver.fromEvent(appointment);
    const interactionLog = stafferNamedDoc
      ? AppointmentRescheduleInteractionBuilder.buildRescheduleInteraction(
          appointment,
          data.event,
          stafferNamedDoc,
          timezone
        )
      : undefined;

    const oldAppointment = await Firestore.getDoc(appointment.ref);
    const eventBefore = SchedulingEvent.buildEventSnapshot(oldAppointment);

    const startTime =
      AppointmentRescheduleInteractionBuilder.getStartTimeChange(
        appointment,
        data.event
      );

    const event = Event.addParticipants(data.event, [
      {
        ...toNamedDocument(patient),
        type: ParticipantType.Patient,
      },
    ]);
    Appointment.replaceEvent(appointment, { ...event });

    if (!isEventable(appointment)) {
      throw new Error('Appointment cannot be left without an event time');
    }

    if (data.waitListItem) {
      appointment.waitListItem = convertWaitListDetailsToItem(
        timezone,
        data.waitListItem
      );
    }

    if (startTime.hasChanged) {
      await Appointment.updateStatus(
        appointment,
        AppointmentStatus.Scheduled,
        InteractionType.AppointmentReschedule,
        stafferNamedDoc
      );
    }

    if (interactionLog) {
      await Appointment.addInteraction(appointment, interactionLog, actor);
    }
    if (data.automations.length) {
      await Appointment.addAutomations(appointment, data.automations);
    }

    const eventAfter = SchedulingEvent.buildEventSnapshot(appointment);
    if (SchedulingEvent.willCauseSchedulingEvent(eventBefore, eventAfter)) {
      const schedulingEvent = SchedulingEvent.init({
        scheduledByActor: actor,
        scheduledByStaffer:
          actor?.type === MentionResourceType.Staffer
            ? (actor.ref as DocumentReference<IStaffer>)
            : undefined,
        scheduledByPractice: data.schedulingEventData.scheduledByPractice,
        reason: data.schedulingEventData.reason,
        reasonSetManually: data.schedulingEventData.reasonSetManually,
        schedulingConditions: data.schedulingEventData.schedulingConditions,
        depositForfeiture: data.schedulingEventData.depositForfeiture,
        eventBefore,
        eventAfter,
      });

      await Appointment.addSchedulingEvent(
        appointment,
        schedulingEvent,
        actor,
        data.schedulingEventData.comments
      );
    }

    if (data.treatmentStep) {
      appointment = {
        ...appointment,
        treatmentPlan: {
          ...appointment.treatmentPlan,
          treatmentStep: {
            ...appointment.treatmentPlan.treatmentStep,
            name: data.treatmentStep.name,
            display: data.treatmentStep.display,
            duration: data.treatmentStep.schedulingRules.duration,
          },
        },
      };
    }

    if (data.depositMetadata) {
      appointment.deposit = {
        ...appointment.deposit,
        metadata: data.depositMetadata,
      };
    }

    appointment.deleted = false;

    const appointmentRef = await Firestore.saveDoc(
      appointment,
      undefined,
      undefined,
      undefined,
      actor
    );
    await Appointment.cancelFollowUp(appointment, actor);

    if (data.checklists) {
      await Appointment.addChecklistItems(appointment, data.checklists);
    }

    if (data.treatmentStep) {
      await Firestore.patchDoc(
        appointment.treatmentPlan.treatmentStep.ref,
        {
          treatments: data.treatmentStep?.treatments,
          display: data.treatmentStep?.display,
          name: data.treatmentStep?.name,
        },
        undefined,
        undefined,
        actor
      );
    }

    return appointmentRef;
  }

  static getCheckInRequirementCount$(
    patient: WithRef<IPatient>,
    appointment: WithRef<IAppointment> | AppointmentSummary
  ): Observable<number> {
    if (!appointment.ref) {
      return of(0);
    }

    return combineLatest([
      PatientForm.allPatientForms$(patient),
      Appointment.checklistItems$({ ref: appointment.ref }, ChecklistType.Pre),
    ]).pipe(
      map(([patientForms, preChecklistItems]) => {
        const unconfirmedForms = patientForms.filter(
          (patientForm) => patientForm.status !== PatientFormStatus.Confirmed
        );

        return preChecklistItems.length + unconfirmedForms.length;
      })
    );
  }

  static async checkOut(
    staffer: WithRef<IStaffer>,
    appointment: WithRef<IAppointment>
  ): Promise<void> {
    await AppointmentManager.issueInvoice(appointment, staffer);
    await Appointment.updateStatus(
      appointment,
      AppointmentStatus.Complete,
      InteractionType.AppointmentComplete,
      stafferToNamedDoc(staffer)
    );
    await Firestore.saveDoc(appointment);
  }

  static async issueInvoice(
    appointment: WithRef<IAppointment>,
    staffer: WithRef<IStaffer>
  ): Promise<void> {
    if (!appointment.invoiceRef) {
      if (Appointment.hasUnlinkedMigratedInvoice(appointment)) {
        return;
      }
      throw new Error('No Invoice to Issue');
    }
    const invoice = await Firestore.getDoc(appointment.invoiceRef);
    if (invoice.status !== InvoiceStatus.Draft) {
      return;
    }
    // TODO: Upon issuing Invoice, tie treatments to invoice.
    await Invoice.issueInvoice(
      invoice,
      await snapshot(Invoice.transactions$(invoice)),
      staffer
    );
  }

  static async markScheduled(
    staffer: WithRef<IStaffer>,
    appointment: WithRef<IAppointment>
  ): Promise<void> {
    await Appointment.updateStatus(
      appointment,
      AppointmentStatus.Scheduled,
      InteractionType.AppointmentBook,
      stafferToNamedDoc(staffer)
    );
    await Firestore.saveDoc(appointment);
  }

  static async markConfirmed(
    staffer: WithRef<IStaffer>,
    appointment: WithRef<IAppointment>
  ): Promise<void> {
    await Appointment.updateStatus(
      appointment,
      AppointmentStatus.Confirmed,
      InteractionType.AppointmentConfirm,
      stafferToNamedDoc(staffer)
    );
    await Firestore.saveDoc(appointment);
  }

  static async markArrived(
    staffer: WithRef<IStaffer>,
    appointment: WithRef<IAppointment>
  ): Promise<void> {
    await Appointment.updateStatus(
      appointment,
      AppointmentStatus.Arrived,
      InteractionType.AppointmentArrived,
      stafferToNamedDoc(staffer)
    );
    await Firestore.saveDoc(appointment);

    await AppointmentManager._sendPopupNotification(
      appointment,
      AppointmentStatus.Arrived
    );
  }

  static async markCheckedIn(
    staffer: WithRef<IStaffer>,
    appointment: WithRef<IAppointment>
  ): Promise<void> {
    await Appointment.updateStatus(
      appointment,
      AppointmentStatus.CheckedIn,
      InteractionType.AppointmentCheckIn,
      stafferToNamedDoc(staffer)
    );
    await Firestore.saveDoc(appointment);

    await AppointmentManager._sendPopupNotification(
      appointment,
      AppointmentStatus.CheckedIn
    );
  }

  static async markCheckingOut(
    staffer: WithRef<IStaffer>,
    appointment: WithRef<IAppointment>
  ): Promise<void> {
    await Appointment.updateStatus(
      appointment,
      AppointmentStatus.CheckingOut,
      InteractionType.AppointmentCheckOut,
      stafferToNamedDoc(staffer)
    );
    await Firestore.patchDoc(appointment.treatmentPlan.treatmentStep.ref, {
      status: TreatmentStepStatus.Incomplete,
    });
    await Firestore.saveDoc(appointment);
  }

  static async markInProgress(
    staffer: WithRef<IStaffer>,
    appointment: WithRef<IAppointment>
  ): Promise<void> {
    await Appointment.updateStatus(
      appointment,
      AppointmentStatus.InProgress,
      InteractionType.AppointmentStart,
      stafferToNamedDoc(staffer)
    );
    await Firestore.patchDoc(appointment.treatmentPlan.treatmentStep.ref, {
      status: TreatmentStepStatus.Incomplete,
    });
    await Firestore.saveDoc(appointment);
  }

  private static async _sendPopupNotification(
    appointment: WithRef<IAppointment>,
    appointmentStatus: AppointmentStatus.Arrived | AppointmentStatus.CheckedIn
  ): Promise<void> {
    if (!appointment.event) {
      return;
    }

    const practiceTimezone = await TimezoneResolver.fromEvent(
      appointment.event
    );

    const appointmentTime = toMomentTz(
      appointment.event.from,
      practiceTimezone
    ).format(TIME_FORMAT);

    const timeUntilAppointment = Event.distance(appointment.event);

    const appointmentPractitioner = await OrganisationCache.staff.get.getDoc(
      appointment.practitioner.ref
    );
    const patient = await Appointment.patient(appointment);
    const brand = await OrganisationCache.brands.getDoc(
      Patient.brandRef(patient)
    );

    const title =
      appointmentStatus === AppointmentStatus.Arrived
        ? 'Patient Arrived'
        : 'Patient Checked In';

    const content =
      appointmentStatus === AppointmentStatus.Arrived
        ? `Patient has arrived for their appointment at ${appointmentTime} (${timeUntilAppointment}).`
        : `Patient has checked in for their appointment at ${appointmentTime} (${timeUntilAppointment}).`;

    const notificationSound =
      appointmentPractitioner.settings?.notifications
        ?.patientArrivedSoundOverride ?? PATIENT_ARRIVED_DEFAULT_SOUND;

    await Firestore.addDoc(
      Staffer.popupNotificationCol(appointmentPractitioner),
      PopupNotification.init({
        title,
        content,
        notificationSound,
        actions: [
          {
            label: 'View Appointment',
            colour: 'primary',
            style: 'flat',
            href: [
              brand.slug,
              'patients',
              patient.ref.id,
              `appointment-history`,
            ],
            fragment: appointment.ref.id,
          },
        ],
      })
    );
  }
}
